/**
 * Het SEPA-batchbestand (pain.001.001.03) met alle uitbetalingen.
 *
 * Eén bestand dat de admin downloadt en bij de bank uploadt; daarna is iedereen
 * betaald. Banken zijn streng op dit formaat, dus de regels die er het vaakst
 * toe leiden dat een bestand geweigerd wordt zitten hier hard in:
 *
 *  - Bedragen in hele centen omgerekend naar euro's met een punt, twee
 *    decimalen. Nooit een komma, nooit een afronding onderweg.
 *  - Het controletotaal moet exact gelijk zijn aan de som van de opdrachten.
 *  - Namen en kenmerken mogen alleen tekens uit de SEPA-tekenset bevatten.
 *  - Een negatief bedrag bestaat niet. Een partij die ons per saldo geld moet
 *    betalen hoort niet in een uitbetalingsbestand; die krijgt een factuur.
 */

import { controleerIban, normaliseerIban } from './iban.js';
import type { Cent } from './geld.js';

export class SepaFout extends Error {
  constructor(bericht: string) {
    super(bericht);
    this.name = 'SepaFout';
  }
}

export interface SepaOpdracht {
  /** Eigen kenmerk, komt terug op het bankafschrift. Hoogstens 35 tekens. */
  kenmerk: string;
  naam: string;
  iban: string;
  bedragCent: Cent;
  /** Wat de ontvanger op zijn afschrift ziet. */
  omschrijving: string;
}

export interface SepaInvoer {
  /** Uniek kenmerk van deze batch. Hoogstens 35 tekens. */
  berichtId: string;
  /** Tijdstip van aanmaken, in UTC. */
  aangemaaktOp: Date;
  /** Datum waarop de bank moet uitvoeren, als YYYY-MM-DD. */
  uitvoerdatum: string;
  opdrachtgeverNaam: string;
  opdrachtgeverIban: string;
  opdrachtgeverBic?: string;
  opdrachten: readonly SepaOpdracht[];
}

export interface SepaBestand {
  xml: string;
  aantalOpdrachten: number;
  totaalCent: Cent;
  /** Opdrachten die er bewust niet in zitten, met de reden erbij. */
  overgeslagen: { naam: string; reden: string }[];
  /** Kenmerken van de opdrachten die er wél in zitten, zodat alleen díe als verstuurd gelden. */
  opgenomen: string[];
  bestandsnaam: string;
}

/** De tekenset die SEPA toestaat. Al het andere wordt vervangen. */
function schoonTekst(invoer: string, maximaleLengte: number): string {
  const vervangen = invoer
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // accenten eraf: é wordt e
    .replace(/[^A-Za-z0-9/\-?:().,'+ ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return vervangen.slice(0, maximaleLengte);
}

function ontsnap(tekst: string): string {
  return tekst
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Centen naar het bedragformaat van de bank: 107650 wordt "1076.50". */
export function centenNaarSepaBedrag(centen: Cent): string {
  if (!Number.isSafeInteger(centen)) {
    throw new SepaFout(`bedrag moet een geheel aantal centen zijn, kreeg ${String(centen)}`);
  }
  if (centen <= 0) {
    throw new SepaFout('een SEPA-opdracht moet meer dan nul zijn');
  }
  const euros = Math.floor(centen / 100);
  const rest = centen % 100;
  return `${euros}.${String(rest).padStart(2, '0')}`;
}

function tijdstip(datum: Date): string {
  // De bank verwacht lokale tijd zonder zone-aanduiding.
  return datum.toISOString().replace(/\.\d{3}Z$/, '');
}

/**
 * Maakt het bestand. Opdrachten die niet kunnen — nul of negatief bedrag, een
 * IBAN met een typefout — komen er niet in en worden apart teruggegeven, zodat
 * de admin ze op het controlescherm ziet in plaats van dat ze stilletjes
 * verdwijnen.
 */
export function maakSepaBestand(invoer: SepaInvoer): SepaBestand {
  const opdrachtgeverIban = controleerIban(invoer.opdrachtgeverIban);
  if (!opdrachtgeverIban.geldig) {
    throw new SepaFout(`Het eigen rekeningnummer klopt niet: ${opdrachtgeverIban.melding ?? ''}`);
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(invoer.uitvoerdatum)) {
    throw new SepaFout('De uitvoerdatum moet als 2026-09-01 worden aangeleverd.');
  }

  const overgeslagen: { naam: string; reden: string }[] = [];
  const bruikbaar: (SepaOpdracht & { iban: string })[] = [];

  for (const opdracht of invoer.opdrachten) {
    if (opdracht.bedragCent <= 0) {
      overgeslagen.push({
        naam: opdracht.naam,
        reden:
          opdracht.bedragCent === 0
            ? 'krijgt niets uitbetaald'
            : 'moet per saldo bijbetalen en krijgt een factuur',
      });
      continue;
    }
    const controle = controleerIban(opdracht.iban);
    if (!controle.geldig) {
      overgeslagen.push({
        naam: opdracht.naam,
        reden: controle.melding ?? 'het rekeningnummer klopt niet',
      });
      continue;
    }
    bruikbaar.push({ ...opdracht, iban: controle.genormaliseerd });
  }

  const totaalCent = bruikbaar.reduce((som, opdracht) => som + opdracht.bedragCent, 0);
  const totaal = bruikbaar.length === 0 ? '0.00' : centenNaarSepaBedrag(totaalCent);

  const opdrachtgever = schoonTekst(invoer.opdrachtgeverNaam, 70);
  const berichtId = schoonTekst(invoer.berichtId, 35);

  const debiteurBank =
    invoer.opdrachtgeverBic != null && invoer.opdrachtgeverBic !== ''
      ? `<DbtrAgt><FinInstnId><BIC>${ontsnap(schoonTekst(invoer.opdrachtgeverBic, 11))}</BIC></FinInstnId></DbtrAgt>`
      : '<DbtrAgt><FinInstnId><Othr><Id>NOTPROVIDED</Id></Othr></FinInstnId></DbtrAgt>';

  const regels = bruikbaar
    .map(
      (opdracht) => `        <CdtTrfTxInf>
          <PmtId><EndToEndId>${ontsnap(schoonTekst(opdracht.kenmerk, 35))}</EndToEndId></PmtId>
          <Amt><InstdAmt Ccy="EUR">${centenNaarSepaBedrag(opdracht.bedragCent)}</InstdAmt></Amt>
          <Cdtr><Nm>${ontsnap(schoonTekst(opdracht.naam, 70))}</Nm></Cdtr>
          <CdtrAcct><Id><IBAN>${normaliseerIban(opdracht.iban)}</IBAN></Id></CdtrAcct>
          <RmtInf><Ustrd>${ontsnap(schoonTekst(opdracht.omschrijving, 140))}</Ustrd></RmtInf>
        </CdtTrfTxInf>`,
    )
    .join('\n');

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.03" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <CstmrCdtTrfInitn>
    <GrpHdr>
      <MsgId>${ontsnap(berichtId)}</MsgId>
      <CreDtTm>${tijdstip(invoer.aangemaaktOp)}</CreDtTm>
      <NbOfTxs>${bruikbaar.length}</NbOfTxs>
      <CtrlSum>${totaal}</CtrlSum>
      <InitgPty><Nm>${ontsnap(opdrachtgever)}</Nm></InitgPty>
    </GrpHdr>
    <PmtInf>
      <PmtInfId>${ontsnap(berichtId)}</PmtInfId>
      <PmtMtd>TRF</PmtMtd>
      <BtchBookg>false</BtchBookg>
      <NbOfTxs>${bruikbaar.length}</NbOfTxs>
      <CtrlSum>${totaal}</CtrlSum>
      <PmtTpInf><SvcLvl><Cd>SEPA</Cd></SvcLvl></PmtTpInf>
      <ReqdExctnDt>${invoer.uitvoerdatum}</ReqdExctnDt>
      <Dbtr><Nm>${ontsnap(opdrachtgever)}</Nm></Dbtr>
      <DbtrAcct><Id><IBAN>${opdrachtgeverIban.genormaliseerd}</IBAN></Id></DbtrAcct>
      ${debiteurBank}
      <ChrgBr>SLEV</ChrgBr>
${regels}
    </PmtInf>
  </CstmrCdtTrfInitn>
</Document>
`;

  return {
    xml,
    aantalOpdrachten: bruikbaar.length,
    totaalCent,
    overgeslagen,
    opgenomen: bruikbaar.map((opdracht) => opdracht.kenmerk),
    bestandsnaam: `uitbetaling-${invoer.uitvoerdatum}-${berichtId.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.xml`,
  };
}
